import { createGLTransaction, GLTransaction, GLTransactionEntry, deleteGLTransaction } from '../firebase/glTransactionServices';
import { getGLAccountById, getGLAccountByNumber } from '../firebase/glAccountServices';
import { getDailyClosingByDate, reopenDailyClosing } from '../firebase/dailyClosingServices';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';

// Interface for daily closing data
export interface DailyClosingData {
  date: Date;
  totalSales: number;
  categorySales: { [key: string]: number }; // e.g., { "Services": 1000, "Products": 500 }
  paymentMethodSales: { [key: string]: number }; // e.g., { "cash": 200, "card": 1300 }
  transactionCount: number;
  notes?: string;
}

// Interface for GL entry preview
export interface GLEntryPreview {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  accountId?: string; // Will be populated when creating actual GL entries
}

// GL Account mapping for payment methods
const PAYMENT_METHOD_GL_ACCOUNTS = {
  'cash': { accountNumber: '1100002', name: 'Cash-in-hand' },
  'card': { accountNumber: '1100001', name: 'CIMB Bank' },
  'qr': { accountNumber: '1100001', name: 'CIMB Bank' },
  'bank_transfer': { accountNumber: '1100001', name: 'CIMB Bank' },
  'other': { accountNumber: '1100003', name: 'Others' }
};

// GL Account mapping for sales categories
const SALES_CATEGORY_GL_ACCOUNTS = {
  'Services': { accountNumber: '6100001', name: 'Store-Services' },
  'Products': { accountNumber: '6100002', name: 'Store-Product' }
};

/**
 * Generate GL entry previews for daily closing
 * 
 * @param dailyClosingData Daily closing data
 * @returns Array of GL entry previews
 */
export const generateGLEntryPreviews = (dailyClosingData: DailyClosingData): GLEntryPreview[] => {
  const entries: GLEntryPreview[] = [];
  
  // Add debit entries for each payment method
  Object.entries(dailyClosingData.paymentMethodSales).forEach(([method, amount]) => {
    if (amount > 0) {
      const accountInfo = PAYMENT_METHOD_GL_ACCOUNTS[method as keyof typeof PAYMENT_METHOD_GL_ACCOUNTS] || 
                         PAYMENT_METHOD_GL_ACCOUNTS['other'];
      
      entries.push({
        accountNumber: accountInfo.accountNumber,
        accountName: accountInfo.name,
        debit: amount,
        credit: 0
      });
    }
  });
  
  // Add credit entries for each sales category
  Object.entries(dailyClosingData.categorySales).forEach(([category, amount]) => {
    if (amount > 0) {
      const accountInfo = SALES_CATEGORY_GL_ACCOUNTS[category as keyof typeof SALES_CATEGORY_GL_ACCOUNTS];
      
      if (accountInfo) {
        entries.push({
          accountNumber: accountInfo.accountNumber,
          accountName: accountInfo.name,
          debit: 0,
          credit: amount
        });
      }
    }
  });
  
  return entries;
};

/**
 * Reverse a daily closing and its GL entries
 * 
 * @param date The date of the daily closing to reverse
 * @returns Promise that resolves when the reversal is complete
 */
export const reverseDailyClosing = async (date: Date): Promise<void> => {
  try {
    // Get the daily closing
    const dailyClosing = await getDailyClosingByDate(date);
    
    if (!dailyClosing) {
      throw new Error(`Daily closing for ${format(date, 'yyyy-MM-dd')} not found`);
    }
    
    // If there's a GL transaction ID, delete the GL transaction
    if (dailyClosing.glTransactionId) {
      await deleteGLTransaction(dailyClosing.glTransactionId);
    }
    
    // Reopen the daily closing
    await reopenDailyClosing(date);
  } catch (error) {
    console.error('Error reversing daily closing:', error);
    throw error;
  }
};

/**
 * Create GL transaction from daily closing data
 * 
 * @param dailyClosingData Daily closing data
 * @param glEntryPreviews GL entry previews (optional - if not provided, will generate from dailyClosingData)
 * @returns Promise with the created GL transaction ID
 */
export const createDailyClosingGLTransaction = async (
  dailyClosingData: DailyClosingData,
  glEntryPreviews?: GLEntryPreview[]
): Promise<string> => {
  try {
    // If no previews provided, generate them
    const entries = glEntryPreviews || generateGLEntryPreviews(dailyClosingData);
    
    // Validate that debits = credits
    const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      throw new Error('GL entries are not balanced. Total debits must equal total credits.');
    }
    
    // Convert entry previews to GL transaction entries
    const glEntries: GLTransactionEntry[] = [];
    
    for (const entry of entries) {
      // Get account ID by account number
      let accountId: string | undefined = entry.accountId;
      
      if (!accountId) {
        // Find the account by account number
        const account = await getGLAccountByNumber(entry.accountNumber);
        if (!account) {
          throw new Error(`GL Account with number ${entry.accountNumber} not found`);
        }
        accountId = account.id;
      }
      
      if (!accountId) {
        throw new Error(`Could not find GL Account ID for account number ${entry.accountNumber}`);
      }
      
      glEntries.push({
        accountId,
        accountNumber: entry.accountNumber,
        accountName: entry.accountName,
        debit: entry.debit,
        credit: entry.credit
      });
    }
    
    // Create the GL transaction
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate: dailyClosingData.date,
      description: `Daily Closing - ${dailyClosingData.date.toLocaleDateString()}`,
      referenceType: 'daily_closing',
      referenceId: dailyClosingData.date.toISOString().split('T')[0], // Use date as reference ID
      entries: glEntries
    };
    
    // Create the transaction in Firestore
    const transactionId = await createGLTransaction(glTransaction);
    return transactionId;
  } catch (error) {
    console.error('Error creating daily closing GL transaction:', error);
    throw error;
  }
};
